<script setup lang="ts">
import moment from 'moment';
import { ref, onMounted } from 'vue';
import NocRepository from '../../repositories/noc.repository';
import DataTable from '@components/DataTable.vue';

const isLoadingNocLogs = ref<boolean>(false);

interface INocLog {
  createdAt: string;
  id: number;
  message: string;
  origin: string;
  request: string;
  response: string;
  statusCode: number;
  updatedAt: string;
}

const logsMailRepository = new NocRepository();
const nocLogsData = ref<INocLog[]>([]);
const showModal = ref<boolean>(false);
const logRequest = ref<any | null>(null);
const logResponse = ref<any | null>(null);
const paginationData = ref();
const currentPage = ref<number>();

const getNocLogsAPI = async (page: number) => {
  isLoadingNocLogs.value = true;
  try {
    const { data } = await logsMailRepository.getlogsMail({ page });
    nocLogsData.value = data.nocLogs ?? [];
    paginationData.value = data.pagination;
  } finally {
    isLoadingNocLogs.value = false;
  }
};

onMounted(async () => {
  getNocLogsAPI(1);
});

const fieldsTable: any = ref([
  { key: 'id', label: 'Id', sortable: true },
  { key: 'origin', label: 'Origin' },
  { key: 'statusCode', label: 'Status Code' },
  { key: 'message', label: 'Mensaje' },
  { 
    key: 'createdAt', 
    label: 'Fecha', 
    formatter: (e: any) => e ? moment(e).format('DD-MM-YYYY HH:mm') : 'Sin fecha' 
  },
  { key: 'actions', label: 'Acciones' },
]);

const onChangePage = async (page: any) => {
  getNocLogsAPI(page);
  currentPage.value = page;
};

const handleOpenModal = (item: INocLog) => {
  showModal.value = true;
  
  try {
    logRequest.value = JSON.parse(item.request);
  } catch (error: any) {
    logRequest.value = { error: "Formato de request inválido", raw: item.request };
  }

  try {
    logResponse.value = JSON.parse(item.response);
  } catch (error) {
    logResponse.value = { error: "Formato de response inválido", raw: item.response };
  }
};

const handleCloseModal = () => {
  showModal.value = false;
  logRequest.value = null;
  logResponse.value = null;
};

// --- File logs tab ---
interface IParsedLine {
  timestamp: string;
  level: string;
  message: string;
}

const activeTab = ref<'noc' | 'logs'>('noc');
const fileLogTypes = ['info', 'error'];
const availableFiles = ref<Record<string, string[]>>({});
const selectedLogType = ref<string>('info');
const selectedLogDate = ref<string>('');
const logLines = ref<IParsedLine[]>([]);
const logLoading = ref<boolean>(false);

const availableDates = () => availableFiles.value[selectedLogType.value] ?? [];

const parseLogLine = (raw: string): IParsedLine => {
  const m = raw.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) (info|error|debug|warn): (.*)$/s);
  if (m) return { timestamp: m[1], level: m[2], message: m[3].trim() };
  return { timestamp: '', level: '', message: raw };
};

const loadLogFile = async () => {
  if (!selectedLogDate.value) return;
  logLoading.value = true;
  const raw = await logsMailRepository.getLogFileContent(selectedLogType.value, selectedLogDate.value);
  logLines.value = raw.map(parseLogLine);
  logLoading.value = false;
};

const loadFileList = async () => {
  availableFiles.value = await logsMailRepository.getLogFiles();
  const dates = availableDates();
  if (dates.length) {
    selectedLogDate.value = dates[0];
    await loadLogFile();
  }
};

const onLogTypeChange = async () => {
  const dates = availableDates();
  selectedLogDate.value = dates[0] ?? '';
  logLines.value = [];
  if (selectedLogDate.value) await loadLogFile();
};

const onLogDateChange = async () => {
  if (selectedLogDate.value) await loadLogFile();
};

const onTabChange = async (tab: 'noc' | 'logs') => {
  activeTab.value = tab;
  if (tab === 'logs' && !Object.keys(availableFiles.value).length) {
    await loadFileList();
  }
};
</script>

<template>
  <Layout title="NOC" :textColor="'color-secondary'">
    <template #layout>
      <!-- Tabs -->
      <div class="d-flex gap-2 mb-3 border-bottom pb-2">
        <button
          :class="['btn btn-sm', activeTab === 'noc' ? 'btn-primary' : 'btn-outline-secondary']"
          @click="onTabChange('noc')"
        >NOC Registros</button>
        <button
          :class="['btn btn-sm', activeTab === 'logs' ? 'btn-primary' : 'btn-outline-secondary']"
          @click="onTabChange('logs')"
        >Logs</button>
      </div>

      <!-- Tab: NOC DB logs (original, sin cambios) -->
      <div v-show="activeTab === 'noc'">
        <DataTable
          :loading="isLoadingNocLogs"
          :fields="fieldsTable"
          :items="nocLogsData"
          @update:model-value-page="onChangePage"
          :totalRows="paginationData?.count"
          :perPage="20"
          :currentPage="paginationData?.current_page"
        >
          <template v-slot:cell(actions)="logsData">
            <button
              class="background-pink p-1 border-0 w-100 text-white rounded-4"
              @click="handleOpenModal(logsData.data.item)"
            >
              Ver Detalle
            </button>
          </template>
        </DataTable>

        <BModal
          :modelValue="showModal"
          hide-footer
          :centered="true"
          @hide="handleCloseModal"
          title="Request & Response Data"
          size="xl"
        >
          <div class="modal-body">
            <div class="json-container">
              <h5>Request:</h5>
              <pre>{{ logRequest }}</pre>
              <h5>Response:</h5>
              <pre>{{ logResponse }}</pre>
            </div>
          </div>
        </BModal>
      </div>

      <!-- Tab: File logs -->
      <div v-show="activeTab === 'logs'">
        <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
          <select
            v-model="selectedLogType"
            class="form-select form-select-sm"
            style="width: auto"
            @change="onLogTypeChange"
          >
            <option v-for="t in fileLogTypes" :key="t" :value="t">{{ t }}</option>
          </select>
          <select
            v-model="selectedLogDate"
            class="form-select form-select-sm"
            style="width: auto"
            :disabled="!availableDates().length"
            @change="onLogDateChange"
          >
            <option v-for="d in availableDates()" :key="d" :value="d">{{ d }}</option>
          </select>
          <span class="text-muted small">{{ logLines.length }} líneas</span>
          <span v-if="logLoading" class="text-muted small ms-1">Cargando...</span>
        </div>

        <div class="log-viewer">
          <div v-if="logLoading" class="log-empty">Cargando...</div>
          <div v-else-if="!logLines.length" class="log-empty">Sin entradas para esta fecha.</div>
          <div
            v-for="(entry, i) in logLines"
            v-else
            :key="i"
            :class="['log-entry', `log-${entry.level || 'unknown'}`]"
          >
            <span v-if="entry.timestamp" class="log-ts">{{ entry.timestamp }}</span>
            <span v-if="entry.level" :class="['log-badge', `badge-${entry.level}`]">{{ entry.level.toUpperCase() }}</span>
            <span class="log-msg">{{ entry.message }}</span>
          </div>
        </div>
      </div>
    </template>
  </Layout>
</template>

<style scoped>
.json-container {
  max-height: 400px;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 5px;
  white-space: pre-wrap;
}

.log-viewer {
  background: #0d1117;
  border-radius: 8px;
  padding: 8px;
  max-height: 580px;
  overflow-y: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12.5px;
}

.log-empty {
  color: #6e7681;
  padding: 12px;
  text-align: center;
}

.log-entry {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 4px;
  border-bottom: 1px solid #161b22;
  line-height: 1.5;
}

.log-entry:hover { background: #161b22; }

.log-ts {
  color: #6e7681;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 11px;
  padding-top: 1px;
}

.log-badge {
  display: inline-block;
  padding: 0px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  margin-top: 2px;
}

.badge-info  { background: #1f6feb; color: #e6edf3; }
.badge-error { background: #b91c1c; color: #fca5a5; }
.badge-debug { background: #2d333b; color: #8b949e; }
.badge-warn  { background: #d97706; color: #fef3c7; }

.log-msg {
  color: #e6edf3;
  white-space: pre-wrap;
  word-break: break-all;
  flex: 1;
}

.log-error .log-msg { color: #fca5a5; }
.log-error .log-ts  { color: #6e4848; }
.log-warn  .log-msg { color: #fde68a; }
</style>
